import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { IInput } from 'interfaces/IInput';
import { IInputTask } from 'interfaces/IInputTask';
import InputService from 'services/InputService';

interface UseUpsertInputTaskArgs {
  activeOrganisationId: string | null;
}

interface UpsertInputTaskVariables {
  inputId: string;
  task: IInputTask;
}

interface UpsertInputTaskContext {
  /** The task as it was before this save, to put back if the save fails. */
  previousTask: IInputTask | null;
}

/**
 * Saves one whole task on any plan — not only the plan open in Planning —
 * and keeps the org's inputs cache in step, so every view reading it (tree,
 * Insights, Planning) updates without a refetch. The same per-type upsert
 * the Tasks grid uses (usePendingTaskSave), without its debounce: callers
 * save on commit.
 *
 * The change shows straight away: the task is written into the cache before
 * the request goes, replaced by the server's copy of the plan on success,
 * and put back as it was on failure. Only that one task is touched either
 * way, so saves to other plans or tasks in flight at the same time aren't
 * undone by a rollback.
 *
 * The task is sent whole, so callers must build it from the latest cached
 * copy of the plan and must not send a second save for the same task while
 * one is in flight.
 */
export const useUpsertInputTask = ({
  activeOrganisationId,
}: UseUpsertInputTaskArgs) => {
  const queryClient = useQueryClient();
  const queryKey = [INPUTS_QUERY_KEY, activeOrganisationId];

  const replaceTask = (inputId: string, task: IInputTask) =>
    queryClient.setQueryData<IInput[]>(queryKey, (current) =>
      current?.map((input) =>
        input.id === inputId
          ? {
              ...input,
              tasks: input.tasks.map((t) =>
                t.taskId === task.taskId ? task : t
              ),
            }
          : input
      )
    );

  return useMutation<
    Awaited<ReturnType<typeof InputService.UpsertTask>>,
    unknown,
    UpsertInputTaskVariables,
    UpsertInputTaskContext
  >({
    mutationFn: async ({ inputId, task }) => {
      const clientNow = new Date().toISOString();
      return InputService.UpsertTask(inputId, task, clientNow);
    },
    onMutate: async ({ inputId, task }) => {
      if (!activeOrganisationId) return { previousTask: null };
      // A refetch landing mid-save would overwrite the change shown below.
      await queryClient.cancelQueries({ queryKey });
      const previousTask =
        queryClient
          .getQueryData<IInput[]>(queryKey)
          ?.find((input) => input.id === inputId)
          ?.tasks.find((t) => t.taskId === task.taskId) ?? null;
      replaceTask(inputId, task);
      return { previousTask };
    },
    onError: (_error, { inputId }, context) => {
      if (activeOrganisationId && context?.previousTask) {
        replaceTask(inputId, context.previousTask);
      }
    },
    onSuccess: (response) => {
      // The server returns the saved plan, so it's written straight in rather
      // than invalidated: a refetch of every plan would be slower and could
      // land on top of another save still in flight.
      const updatedInput = response?.data?.[0];
      if (!updatedInput || !activeOrganisationId) return;

      queryClient.setQueryData<IInput[]>(queryKey, (current) => {
        if (!current) return current;
        return current.map((item) =>
          item.id === updatedInput.id ? updatedInput : item
        );
      });
    },
  });
};

export default useUpsertInputTask;
